/* NATIVA · <cat-slider>: horizontal scroll-snap row with arrows and optional autoplay.
   Markup: [data-cat-track] holding [data-cat-slide] items, [data-cat-nav] wrapping
   [data-cat-prev] / [data-cat-next], and data-autoplay (seconds) on the element.
   Used by the Featured categories and Jar showcase sections. */
if (!customElements.get('cat-slider')) {
  customElements.define('cat-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cat-track]');
      this.prev = this.querySelector('[data-cat-prev]');
      this.next = this.querySelector('[data-cat-next]');
      this.nav = this.querySelector('[data-cat-nav]');
      this.slides = Array.from(this.querySelectorAll('[data-cat-slide]'));
      if (!this.track || !this.slides.length) return;

      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.update = this.update.bind(this);
      this.prev.addEventListener('click', () => this.go(-1));
      this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => requestAnimationFrame(this.update), { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.target !== this.track || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
        e.preventDefault();
        this.go((e.key === 'ArrowRight') !== this.rtl ? 1 : -1);
      });
      this.ro = new ResizeObserver(this.update);
      this.ro.observe(this.track);

      // Theme editor: bring the selected category into view and hold still.
      this.onSelect = (e) => {
        if (!this.contains(e.target)) return;
        this.paused = true;
        this.stop();
        this.scrollToSlide(this.slides.indexOf(e.target));
      };
      this.onDeselect = (e) => {
        if (!this.contains(e.target)) return;
        this.paused = false;
        this.start();
      };
      document.addEventListener('shopify:block:select', this.onSelect);
      document.addEventListener('shopify:block:deselect', this.onDeselect);

      // Autoplay: pauses while hovered with a mouse, focused, touched or in
      // a hidden tab. Taps on phones fire no mouseleave, so hover is only
      // tracked for real mice and touch resumes when the finger lifts.
      this.delay = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;
      if (this.delay && !(window.Shopify && Shopify.designMode)) {
        const pause = () => { this.paused = true; this.stop(); };
        const resume = () => { this.paused = false; this.start(); };
        this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') pause(); });
        this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') resume(); });
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.addEventListener('touchstart', pause, { passive: true });
        this.addEventListener('touchend', resume, { passive: true });
        this.onVis = () => (document.hidden ? this.stop() : this.start());
        document.addEventListener('visibilitychange', this.onVis);
        this.start();
      }
      this.update();
    }

    disconnectedCallback() {
      this.stop();
      if (this.ro) this.ro.disconnect();
      document.removeEventListener('shopify:block:select', this.onSelect);
      document.removeEventListener('shopify:block:deselect', this.onDeselect);
      if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
    }

    // Distance scrolled from the start edge; positive in LTR and RTL.
    get pos() { return Math.abs(this.track.scrollLeft); }
    get max() { return this.track.scrollWidth - this.track.clientWidth; }
    get step() {
      const a = this.slides[0], b = this.slides[1];
      return b ? Math.abs(b.offsetLeft - a.offsetLeft) : this.track.clientWidth;
    }

    go(dir) {
      this.track.scrollBy({ left: dir * this.step * (this.rtl ? -1 : 1) });
    }

    scrollToSlide(i) {
      if (i < 0) return;
      this.track.scrollTo({ left: i * this.step * (this.rtl ? -1 : 1) });
    }

    update() {
      const max = this.max;
      this.nav.hidden = max <= 2;
      this.prev.disabled = this.pos <= 2;
      this.next.disabled = this.pos >= max - 2;
    }

    start() {
      if (!this.delay || this.paused || this.timer) return;
      this.timer = setInterval(() => {
        if (this.max <= 2) return;
        if (this.pos >= this.max - 2) this.scrollToSlide(0);
        else this.go(1);
      }, this.delay);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }
  });
}
